<!--
ZSGrid Component - Advanced Data Grid with State Persistence

New Features:
- keepState: Automatically saves and restores filter and pagination state to localStorage
- id: Unique identifier required when keepState is enabled
- resizable: Enables column resizing via drag handles on both header and body

State Persistence:
- Saves filterObj and currentPage to localStorage with keys: zsGrid_{id}_filters and zsGrid_{id}_page
- Loads state on mount if props are not provided (filterObj empty and currentPage is 1)
- Automatically saves state when filters or pagination change
- Emits appropriate events to sync with parent component

Column Resizing:
- Enable globally with resizable prop
- Control per column with resize: true/false in column definition
- Drag the resize handle on the right edge of column headers or body cells
- Minimum column width is 50px
- Column widths are persisted in localStorage with key: zsGrid_{id}_columnWidths
- Widths are restored on component mount

Usage:
<ZSGrid 
  :data="data"
  :cols="columns"
  keep-state
  id="unique-grid-id"
  paginator
  resizable
  v-model:filter-obj="filters"
  :current-page="currentPage"
/>

Column Definition Example:
{
  field: 'name',
  label: 'Name',
  sort: true,
  filter: { type: 'text' },
  resize: true  // Enable resizing for this column
}

Methods:
- clearSavedState(): Clears saved state from localStorage (filters, page, sort)
- clearColumnWidths(): Clears saved column widths from localStorage
-->

<template>
  <div class="zs-grid" :class="{ stripped: stripped, 'fixed-header': height }">
    <table>
      <thead class="zs-grid-header">
        <tr class="zs-grid-header-row" ref="headerRow">
          <th v-if="selectMode !== 'none'" class="select-column">
            <ZSCheckBox v-if="selectMode === 'multi'" :modelValue="isAllSelected" @update:modelValue="toggleSelectAll"
              alignment="middle" label="" :indeterminate="isIndeterminate" />
          </th>
          <th :style="getColumnStyle(column)" v-for="(column, index) in columns" :key="getColumnKey(column, index)">
            <slot :name="column.field + '-header'" :column="column">
              <div class="column-header">
                <ZSIcon v-if="column.sort" class="column-header-icon" @click="sort(column)"
                  v-bind="getColumnSortData(column)" />
                <div @click="sort(column)" :class="{ sortable: column.sort }" class="column-label">{{ column.label }}
                </div>

                <ZSDropdown v-if="column.filter" :align="column?.filter?.align ?? 'center'">
                  <template #trigger="{ toggle, isOpen }">
                    <ZSIcon color="red" @click="toggle" icon="Filter" v-bind="getFilterIconData(column)" />
                  </template>
                  <template #default="{ toggle, isOpen }">
                    <ZSGridFilter @filter="onFilter(toggle, $event)" :model-value="filterObj[column.field]?.value"
                      :column="column" />
                  </template>
                </ZSDropdown>

                <div v-if="isColumnResizable(column)" class="resize-handle"
                  @mousedown="startResize($event, column, index)"></div>
              </div>

            </slot>
          </th>
        </tr>
        <tr>
          <th :colspan="selectMode !== 'none' ? columns.length + 1 : columns.length">
            <slot name="filter-list">
              <div class="filter-list">
                <div v-for="filter in filterOptions" :key="filter.key" class="chip">
                  <span>{{ filter.str }}</span>
                  <ZSIcon style="cursor: pointer;" @click="removeFilter(filter.key)" color="white" icon="Close">
                  </ZSIcon>
                </div>
              </div>
            </slot>
          </th>
        </tr>
      </thead>
      <tbody v-if="hasData" :style="tbodyStyle">
        <template v-for="item in data" :key="item.id">
          <tr @click="onRowClick(item)"
            :class="{ 'row-hover': showHover, 'selected-row': isSelected(item), 'has-custom-row-class': getRowClass(item), [getRowClass(item)]: getRowClass(item) }"
            :style="{ cursor: rowSelect ? 'pointer' : 'default' }">
            <td v-if="selectMode !== 'none'" class="select-column">
              <ZSCheckBox v-if="selectMode === 'multi'" :modelValue="isSelected(item)"
                @update:modelValue="(value) => toggleSelect(item, value)" alignment="middle" label="" />
              <ZSRadioButton v-else-if="selectMode === 'single'" color="orange" :value="item[itemKey]" />
            </td>
            <td :style="getColumnStyle(column)" v-for="(column, index) in columns" :key="getColumnKey(column, index)"
              :data-label="column.label">
              <slot :name="column.field" :expanded="isExpanded(item)"  :toggle="toggleRowDetails" :item="item">
                <div class="zsgrid-item-value">{{ getItemFieldValue(item, column.field) }}</div>
              </slot>
            </td>
          </tr>
          <tr v-if="detail" v-show="isExpanded(item)" class="detail-row">
            <td :colspan="selectMode !== 'none' ? columns.length + 1 : columns.length">
              <slot name="detail" :toggle="toggleRowDetails" :item="item">
                <div style="padding: 1rem; font-style: italic;">
                  No details available
                </div>
              </slot>
            </td>
          </tr>
        </template>
      </tbody>
      <tbody :style="tbodyStyle" v-else>
        <tr>
          <td :colspan="selectMode !== 'none' ? columns.length + 1 : columns.length">
            <div class="nodata">
              <slot name="empty-message">
                {{ t('components.no_data') }}
              </slot>
            </div>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <slot v-if="footer" name="footer">
          <tr class="footer-row">
            <td v-if="selectMode !== 'none'" class="select-column"></td>
            <td :style="getColumnStyle(column)" v-for="(column, index) in columns" :key="getColumnKey(column, index)">
              <slot :name="column.field + '-footer'" :column="column">
              </slot>
            </td>
          </tr>
        </slot>
        <slot v-if="paginator" name="paginator">
          <tr>
            <td :colspan="selectMode !== 'none' ? columns.length + 1 : columns.length">
              <div class="paginator-layout">
                <slot name="paginator-left"></slot>
                <div :class="{ [`align-${paginatorAlign}`]: true }" class="paginator-holder">
                  <ZSPaginator v-if="!infinitScroll" ref="paginator1" @page-changed="onPageChanged"
                    :total-items="titems" :items-per-page="itemsPerPage" :current-page="currentPage" v-bind="attrs"></ZSPaginator>
                  <ZSButton color="orange" v-else-if="canLoadMore" @click="loadMore">{{ $t('components.load_more') }}
                  </ZSButton>
                </div>
                <slot name="paginator-right"></slot>
              </div>
            </td>
          </tr>
        </slot>
      </tfoot>
    </table>
    <Loader v-if="busy"></Loader>
    <slot></slot>
  </div>
</template>

<script setup>
import { ref, computed, provide, useAttrs, watch, onMounted, onUpdated, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import ZSPaginator from './ZSPaginator.vue';
import Loader from './Loader.vue';
import ZSGridFilter from './filter/ZSGridFilter.vue';
import ZSIcon from '../global/ZSIcon.vue';
import ZSCheckBox from '../form/ZSCheckBox.vue';
import ZSRadioButton from '../form/ZSRadioButton.vue';
import ZSDropdown from '../ZSDropdown.vue';
import ZSButton from '../ZSButton.vue';
import storage from '@/services/storage.js';

// 2. Create the template ref
const headerRow = ref(null);

// Define props
const props = defineProps({
  data: {
    type: Array,
    default: () => []
  },
  cols: {
    type: Array,
    required: true
  },
  height: {
    type: String,
    default: null
  },
  visibleCols: {
    type: Array,
    default: () => null
  },
  paginator: {
    type: Boolean,
    default: false
  },
  infinitScroll: {
    type: Boolean,
    default: false
  },
  selectMode: {
    type: String,
    default: 'none',
    validator: v => ['none', 'single', 'multi'].includes(v)
  },
  modelValue: {
    type: Array,
    default: () => []
  },
  filterObj: {
    type: Object,
    default: () => ({})
  },
  sort: {
    type: Array,
    default: () => []
  },
  selfPaginate: {
    type: Boolean,
    default: false
  },
  totalItems: {
    type: Number,
    default: null
  },
  currentPage: {
    type: Number,
    default: 1
  },
  itemsPerPage: {
    type: Number,
    default: 10
  },
  itemKey: {
    type: String,
    default: 'id'
  },
  stripped: {
    type: Boolean,
    default: false
  },
  paginatorAlign: {
    type: String,
    default: 'center'
  },
  busy: {
    type: Boolean,
    default: false
  },
  footer: {
    type: Boolean,
    default: false
  },
  showHover: {
    type: Boolean,
    default: true
  },
  rowClass: {
    type: [String, Function],
    default: null
  },
  detail: {
    type: Boolean,
    default: true
  },
  keepState: {
    type: Boolean,
    default: false
  },
  id: {
    type: String,
    default: null
  },
  resizable: {
    type: Boolean,
    default: false
  },
  rowSelect: {
    type: Boolean,
    default: false
  },
  selectedRowClass: {
    type: [String, Function],
    default: 'selected-row'
  }
});



// Define emits
const emit = defineEmits(['page-changed', 'update:modelValue', 'filter', 'update:filterObj', 'update:sort', 'row-select']);

const checkHeaderContentOverflow = () => {
  if (!headerRow.value) return;

  const headers = headerRow.value.querySelectorAll('th');

  for (const header of headers) {
    // Check for horizontal overflow
    if (header.scrollWidth > header.clientWidth) {
      header.classList.add('has-overflow');
      // Optional: Add a title attribute to show the full text on hover
      header.setAttribute('title', header.textContent.trim());
    } else {
      header.classList.remove('has-overflow');
      header.removeAttribute('title');
    }
  }
};

// 2. Create a single function to run all checks for cleanliness
const runAllGridChecks = () => {
  checkHeaderContentOverflow();
};


// Setup reactive state
const attrs = useAttrs();
const { t } = useI18n();
const selectedItems = ref(props.modelValue);
const sortArray = ref(props.sort);

const tbodyStyle = computed(() => {
  if (props.height) {
    // On mobile, height is controlled by the media query's rules for the container
    if (window.innerWidth <= 768) {
      return {};
    }
    return {
      height: props.height,
    };
  }
  return {};
});

const columns = computed(() => {
  if (props.visibleCols === null) {
    return props.cols;
  }
  return props.cols.filter((col, index) => props.visibleCols.includes(index));
});
const titems = computed(() => {
  return props.totalItems || (props.data?.length ?? 0)
})
const currentPage = ref(props.currentPage || 1);


const paginator1 = ref(null);

const filterObj = ref(props.filterObj)

// State persistence helpers
const getStorageKey = (suffix) => {
  if (!props.keepState || !props.id) return null;
  return `zsGrid_${props.id}_${suffix}`;
};

const saveState = () => {
  if (!props.keepState || !props.id) return;

  const filterKey = getStorageKey('filters');
  const pageKey = getStorageKey('page');
  const sortKey = getStorageKey('sort');

  storage.setJSONItem(filterKey, filterObj.value);
  storage.setJSONItem(pageKey, currentPage.value);
  storage.setJSONItem(sortKey, sortArray.value);
};

const loadState = () => {
  if (!props.keepState || !props.id) return false;

  const filterKey = getStorageKey('filters');
  const pageKey = getStorageKey('page');
  const sortKey = getStorageKey('sort');

  const savedFilters = storage.getJSONItem(filterKey, null);
  const savedPage = storage.getJSONItem(pageKey, null);
  const savedSort = storage.getJSONItem(sortKey, null);

  let stateLoaded = false;

  // Load filters if not provided via props and saved state exists
  if (Object.keys(props.filterObj).length === 0 && savedFilters && Object.keys(savedFilters).length > 0) {
    filterObj.value = savedFilters;
    emit('update:filterObj', savedFilters);
    emit('filter', savedFilters);
    stateLoaded = true;
  }

  // Load page if not provided via props and saved state exists
  if (props.currentPage === 1 && savedPage && savedPage > 1) {
    currentPage.value = savedPage;
    if (paginator1.value) {
      paginator1.value.currentPage = savedPage;
    }
    emit('page-changed', {
      page: savedPage,
      perPage: props.itemsPerPage
    });
    stateLoaded = true;
  }

  // Load sort if not provided via props and saved state exists
  if (props.sort.length === 0 && savedSort && savedSort.length > 0) {
    sortArray.value = savedSort;
    emit('update:sort', savedSort);
    stateLoaded = true;
  }

  return stateLoaded;
};

const clearSavedState = () => {
  if (!props.keepState || !props.id) return;

  const filterKey = getStorageKey('filters');
  const pageKey = getStorageKey('page');
  const sortKey = getStorageKey('sort');

  storage.removeItem(filterKey);
  storage.removeItem(pageKey);
  storage.removeItem(sortKey);
};

const filterOptions = computed(() => {
  const filter = []
  for (let k of Object.keys(filterObj.value)) {
    let val = filterObj.value[k]
    if (val) {
      let v = val?.value
      if (val?.label) v = val.label

      const show = (val?.show ?? true)
      if (show) filter.push({
        str: val?.column?.label + " = " + v,
        key: k
      })
    }
  }

  return filter;

})


const removeFilter = (key) => {

  delete filterObj.value[key]
  emit('filter', filterObj.value)
  emit('update:filterObj', filterObj.value)

  // Save state after filter removal
  if (props.keepState) {
    saveState();
  }
}


const getFilterIconData = (column) => {
  let color = "var(--color-orange)"
  if (filterObj.value[column.field] && filterObj.value[column.field].value) {
    color = "var(--color-green)"
  }
  return {
    color: color,
    size: color == "var(--color-orange)" ? 'small' : 'small'
  }
}

const onFilter = (closefn, value) => {



  filterObj.value[value.column.field] = value;


  if (!value?.value) {
    if (value?.value !== 0) {
      delete filterObj.value[value.column.field]
    }
  }
  emit('filter', filterObj.value)
  emit('update:filterObj', filterObj.value)
  closefn(true)

  // Save state after filtering
  if (props.keepState) {
    saveState();
  }
}


watch(() => props.modelValue, (newVal) => {
  selectedItems.value = newVal;
}, { deep: true });

// Watch for external changes to filterObj and save state
watch(() => props.filterObj, (newVal) => {
  filterObj.value = newVal;
  if (props.keepState && props.id) {
    saveState();
  }
}, { deep: true });

// Watch for external changes to currentPage and save state
watch(() => props.currentPage, (newVal) => {
  if (paginator1.value) {
    paginator1.value.currentPage = newVal;
  }
  currentPage.value = newVal;
  if (props.keepState && props.id) {
    saveState();
  }
});

watch(selectedItems, (newVal) => {
  emit('update:modelValue', newVal);
}, { deep: true });

watch(() => props.sort, (newVal) => {
  sortArray.value = newVal;
  if (props.keepState && props.id) {
    saveState();
  }
}, { deep: true });

watch(sortArray, (newVal) => {
  emit('update:sort', newVal);
}, { deep: true });

const hasData = computed(() => (props.data?.length ?? 0) > 0);

const isAllSelected = computed(() => {
  const len = props.data?.length ?? 0;
  return props.selectMode === 'multi' && len > 0 && selectedItems.value.length === len;
});

const isIndeterminate = computed(() => {
  const len = props.data?.length ?? 0;
  return props.selectMode === 'multi' && selectedItems.value.length > 0 && selectedItems.value.length < len;
});

const data = computed(() => {
  if (props.selfPaginate && props.paginator && paginator1.value) {
    console.log('Paginating data', currentPage.value, paginator1.value.itemsPerPage);

    return (props.data ?? []).slice((paginator1.value.currentPage - 1) * paginator1.value.itemsPerPage, paginator1.value.currentPage * paginator1.value.itemsPerPage);
  }
  return props.data ?? [];
});

const getColumnKey = (column, index) => {
  return column.field || index;
};

const selectedRow = ref(null);

const getRowClass = (item) => {
  const classes = [];

  // Add custom rowClass if provided
  if (props.rowClass) {
    if (typeof props.rowClass === 'function') {
      const customClass = props.rowClass(item);
      if (customClass) classes.push(customClass);
    } else {
      classes.push(props.rowClass);
    }
  }

  // Add selectedRowClass if this row is selected
  if (props.rowSelect && selectedRow.value && selectedRow.value[props.itemKey] === item[props.itemKey]) {
    if (typeof props.selectedRowClass === 'function') {
      const selectedClass = props.selectedRowClass(item);
      if (selectedClass) classes.push(selectedClass);
    } else {
      classes.push(props.selectedRowClass);
    }
  }

  return classes.join(' ');
};

const onRowClick = (item) => {
  if (!props.rowSelect) return;
  selectedRow.value = item;
  emit('row-select', item);
};

const getItemFieldValue = (item, field) => {
  const fields = field.split('.');
  let value = item;
  for (const f of fields) {
    if (value === null || value === undefined) return '';
    value = value[f];
  }
  return value;
};

const addColumn = (column) => {
  console.warn('addColumn method needs to be updated to work with visibleCols prop');
};

const onPageChanged = (pageData) => {
  const page = typeof pageData === 'object' ? pageData.page : pageData;
  currentPage.value = page;
  emit('page-changed', pageData);

  // Save state after page change
  if (props.keepState) {
    saveState();
  }
};

const canLoadMore = computed(() => {
  if (!props.totalItems) return true;
  const itemsPerPage = props.itemsPerPage;
  const totalPages = Math.ceil(props.totalItems / itemsPerPage);
  return currentPage.value < totalPages;
});

const loadMore = () => {
  const nextPage = currentPage.value + 1;
  // The onPageChanged function expects an object like the paginator emits it
  onPageChanged({ page: nextPage, perPage: props.itemsPerPage });
};

const isSelected = (item) => {
  return selectedItems.value.some(selectedItem => selectedItem[props.itemKey] === item[props.itemKey]);
};

const toggleSelect = (item, value) => {
  if (props.selectMode === 'single') {
    if (value) {
      selectedItems.value = [item];
    }
  } else if (props.selectMode === 'multi') {
    const index = selectedItems.value.findIndex(selectedItem => selectedItem[props.itemKey] === item[props.itemKey]);
    if (value && index === -1) {
      selectedItems.value.push(item);
    } else if (!value && index !== -1) {
      selectedItems.value.splice(index, 1);
    }
  }
};

const toggleSelectAll = (value) => {
  if (props.selectMode !== 'multi') return;
  if (!value) {
    selectedItems.value = [];
  } else {
    selectedItems.value = [...props.data];
  }
};

provide('addColumn', addColumn);

const getLabel = (defaultText, context) => {
  return defaultText;
};

const sort = (column) => {
  if (!column.sort) return;
  const sortIndex = sortArray.value.findIndex(s => s.field === column.field);
  if (sortIndex > -1) {
    if (sortArray.value[sortIndex].direction === 'ASC') {
      sortArray.value[sortIndex].direction = 'DESC';
    } else {
      sortArray.value.splice(sortIndex, 1);
    }
  } else {
    sortArray.value = [{ field: column.field, direction: 'ASC' }];
  }

  // Save state after sorting
  if (props.keepState) {
    saveState();
  }
};

const getColumnSortData = (column) => {
  let sortIcon = "SortUpDown"
  const sortItem = sortArray.value.find(s => s.field === column.field);
  if (sortItem) {
    sortIcon = sortItem.direction !== 'ASC' ? 'SortDown' : 'SortUp';
  }

  const d = {
    icon: sortIcon,
    color: sortIcon == 'SortUpDown' ? 'var(--color-orange)' : 'var(--color-green)',
    size: sortIcon == 'SortUpDown' ? 'small' : 'small',
  }

  return d
};

const expandedRows = ref(new Set());

const toggleRowDetails = (item) => {
  const key = item[props.itemKey];
  expandedRows.value.has(key)
    ? expandedRows.value.delete(key)
    : expandedRows.value.add(key);
};

const isExpanded = (item) =>
  expandedRows.value.has(item[props.itemKey]);

// Column width storage key
const getColumnWidthsStorageKey = () => {
  if (!props.resizable || !props.id) return null;
  return `zsGrid_${props.id}_columnWidths`;
};

// Store column widths in a map for easy access
const columnWidths = ref(new Map());

// Load column widths from localStorage
const loadColumnWidths = () => {
  const key = getColumnWidthsStorageKey();
  if (!key) return;

  const saved = storage.getJSONItem(key, null);
  if (saved && typeof saved === 'object') {
    columnWidths.value = new Map(Object.entries(saved));
  }
};

// Save column widths to localStorage
const saveColumnWidths = () => {
  const key = getColumnWidthsStorageKey();
  if (!key) return;

  const widthsObj = Object.fromEntries(columnWidths.value);
  storage.setJSONItem(key, widthsObj);
};

// Get style for a column (including width if it was resized)
const getColumnStyle = (column) => {
  const fieldKey = column.field || column.label;
  const width = props.resizable ? columnWidths.value.get(fieldKey) : null;
  
  let baseStyle = {};
  if (column.style) {
    if (typeof column.style === 'string') {
      // Parse string style like "width: 8%; color: red;"
      baseStyle = column.style.split(';').reduce((acc, o) => {
        const [k, v] = o.split(':');
        if (k && v) acc[k.trim()] = v.trim();
        return acc;
      }, {});
    } else if (typeof column.style === 'object') {
      // Use object style directly
      baseStyle = { ...column.style };
    }
  }

  return {
    ...baseStyle,
    width: width ? `${width}px` : (baseStyle.width || undefined)
  };
};

// Column resizing functionality
const isResizing = ref(false);
const resizingColumn = ref(null);
const startX = ref(0);
const startWidth = ref(0);

const startResize = (event, column, index) => {
  if (!props.resizable || column.resize === false) return;

  event.preventDefault();
  event.stopPropagation();

  isResizing.value = true;
  resizingColumn.value = index;
  startX.value = event.pageX;

  const th = event.target.closest('th');
  startWidth.value = th.offsetWidth;

  document.addEventListener('mousemove', handleResize);
  document.addEventListener('mouseup', stopResize);
  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';
};

const handleResize = (event) => {
  if (!isResizing.value || resizingColumn.value === null) return;

  const diff = event.pageX - startX.value;
  const newWidth = Math.max(50, startWidth.value + diff); // Minimum width of 50px

  const column = columns.value[resizingColumn.value];
  if (column) {
    const fieldKey = column.field || column.label;
    columnWidths.value.set(fieldKey, newWidth);
  }
};

const stopResize = () => {
  isResizing.value = false;
  resizingColumn.value = null;

  document.removeEventListener('mousemove', handleResize);
  document.removeEventListener('mouseup', stopResize);
  document.body.style.cursor = '';
  document.body.style.userSelect = '';

  // Save column widths after resizing
  if (props.resizable) {
    saveColumnWidths();
  }
};

const isColumnResizable = (column) => {
  if (!props.resizable) return false;
  if (!(column.resize && column.resize === true) || column.resize === false) return false;

  
  return true;
};

// Clear saved column widths
const clearColumnWidths = () => {
  const key = getColumnWidthsStorageKey();
  if (key) {
    storage.removeItem(key);
  }
  columnWidths.value.clear();
};

/**
 * Hides all empty detail sections by collapsing rows that have no content
 * in their detail slot. This checks for actual rendered content (not just whitespace).
 */
const hideAllEmptyDetailSections = () => {
  if (!props.detail) return;

  const table = headerRow.value?.closest('table');
  if (!table) return;

  const detailRows = table.querySelectorAll('.detail-row');

  detailRows.forEach((row) => {
    const td = row.querySelector('td');
    if (!td) return;

    // Check if the detail content is empty (only has whitespace or default "No details available" message)
    const textContent = td.textContent?.trim() || '';
    const hasSlotContent = td.querySelector('[class]:not(.detail-row)') !== null ||
      td.children.length > 1 ||
      (td.children.length === 1 && td.children[0].children.length > 0);

    // If content is just whitespace or the default message, consider it empty
    const isEmpty = !hasSlotContent &&
      (!textContent || textContent === 'No details available');

    if (isEmpty && row.style.display !== 'none') {
      // Find the corresponding item and collapse it
      const previousRow = row.previousElementSibling;
      if (previousRow) {
        // Try to find the item key from the previous row's data
        const itemElements = Array.from(previousRow.querySelectorAll('td'));
        // Collapse the detail by removing it from expandedRows
        (props.data ?? []).forEach(item => {
          if (isExpanded(item) && row.style.display !== 'none') {
            expandedRows.value.delete(item[props.itemKey]);
          }
        });
      }
    }
  });
};

// Load state from localStorage on component mount
onMounted(() => {
  runAllGridChecks();
  window.addEventListener('resize', runAllGridChecks);
  if (props.keepState) {
    if (!props.id) {
      console.warn('ZSGrid: keepState is enabled but no id prop is provided. State will not be persisted.');
      return;
    }
    loadState();
  }
  if (props.resizable) {
    loadColumnWidths();
  }
});

onUpdated(() => {
  runAllGridChecks();
});


onUnmounted(() => {
  window.removeEventListener('resize', runAllGridChecks);
});


defineExpose({
  toggleRowDetails,
  clearSavedState,
  clearColumnWidths,
  runAllGridChecks,
  hideAllEmptyDetailSections
});

</script>

<style>
:root {
  --color-orange: #EB7629;
  /* --color-green: #27AE60; */
  --color-white: #FFFFFF;
  --color-blue: #4384BD;
  --color-dark-blue: #32638D;
  --color-light-blue: #D0E0EE;
  --color-darker-blue: #bfcbd6;
  --color-grid-header-text: #538ec2;
  --color-grid-header-border: #D0E0EE;
  --color-grid-border-top: red;
  --color-grid-nodata-text: #aaaaaa;
  --color-grid-td-text: #32638D;
  --color-grid-header-bg: var(--color-white);
  --zs-grid-width: 100%;
  --zs-grid-border-collapse: collapse;
  --zs-grid-thead-border-bottom: 10px solid transparent;
  --zs-grid-th-font-size: 12px;
  --zs-grid-th-font-family: "Poppins";
  --zs-grid-th-color: var(--color-grid-header-text);
  --zs-grid-th-text-align: left;
  --zs-grid-th-border-left: 1px solid var(--color-grid-header-border);
  --zs-grid-th-padding-left: 2em;
  --zs-grid-header-border: 1px solid var(--color-grid-header-border);
  --zs-grid-td-font-size: 12px;
  --zs-grid-td-font-family: "Poppins";
  --zs-grid-td-color: var(--color-grid-td-text);
  --zs-grid-td-height: 44px;
  --zs-grid-td-min-height: 44px;
  --zs-grid-td-text-align: left;
  --zs-grid-tbody-first-child-border-top: 1px solid var(--color-grid-border-top);
  --zs-grid-tbody-last-child-margin-bottom: 10px;
  --zs-grid-nodata-text-align: center;
  --zs-grid-nodata-padding: 10px;
  --zs-grid-nodata-font-size: 12px;
  --zs-grid-nodata-color: var(--color-grid-nodata-text);
  --zs-grid-select-column-width: 60px;
  --zs-grid-select-column-text-align: center;
  --zs-grid-select-column-padding-right: 5px;
  --zs-grid-checkbox-cursor: pointer;
  --zs-grid-overflow-bg: rgb(234, 234, 234);
  --zs-grid-overflow-color: #c0392b;
}

.zs-grid {
  width: var(--zs-grid-width);
  overflow: auto;
}

.zs-grid table {
  table-layout: fixed;
  width: var(--zs-grid-width);
  border-collapse: var(--zs-grid-border-collapse);
}

.zs-grid.stripped tbody tr:nth-child(even):not(.has-custom-row-class) {
  background-color: #f4f4f4;
}

.zs-grid.stripped tbody tr:nth-child(odd):not(.has-custom-row-class) {
  background-color: #ffffff;
}

.zs-grid thead {
  border-bottom: var(--zs-grid-thead-border-bottom);
}

.zs-grid tbody tr:first-child {
  border-top: var(--zs-grid-tbody-first-child-border-top);
}

.zs-grid tbody tr:last-child {
  margin-bottom: var(--zs-grid-tbody-last-child-margin-bottom);
}

.zs-grid td {
  font-size: 0.9em;
  font-family: var(--zs-grid-td-font-family);
  color: #333;
  border-bottom: 1px solid lightgray;
  height: var(--zs-grid-td-height);
  min-height: var(--zs-grid-td-min-height);
  text-align: var(--zs-grid-td-text-align);

}

.zs-grid th {
  font-size: 1em;
  font-family: var(--zs-grid-th-font-family);
  color: black;
  text-align: var(--zs-grid-th-text-align);
  background-color: var(--color-grid-header-bg);
  /* This is crucial for the overflow detection to work */
  /* overflow: hidden;
  position: relative; Needed for the ::after pseudo-element positioning   */
}

.zs-grid th .column-label {
  user-select: none;
}

.zs-grid th .column-label.sortable {
  cursor: pointer;
}


.column-header-icon {
  cursor: pointer;
}


.nodata {
  text-align: var(--zs-grid-nodata-text-align);
  padding: var(--zs-grid-nodata-padding);
  font-size: var(--zs-grid-nodata-font-size);
  color: var(--color-grid-nodata-color);
}

.select-column {
  width: var(--zs-grid-select-column-width);
  text-align: var(--zs-grid-select-column-text-align);
  padding-right: var(--zs-grid-select-column-padding-right);
}

/* td.select-column {
  padding-left: 1.4em;
} */

.select-column input[type="checkbox"] {
  cursor: var(--zs-grid-checkbox-cursor);
}


.filter-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6em;
  font-size: 80%;
  font-weight: 400;
  margin-top: 0.5em;
}

.filter-list .chip {
  display: flex;
  align-items: center;
  gap: 0.5em;
  background-color: var(--color-orange);
  color: white;
  padding: 0.25em 0.5em;
  border-radius: 0.25em;
}

.paginator-layout {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.paginator-holder {
  display: flex;
  align-items: center;
  flex: 1;
}

.paginator-holder.align-right {
  justify-content: flex-end;
}

.paginator-holder.align-center {
  justify-content: center;
}

.paginator-holder.align-left {
  justify-content: flex-start;
}

.zs-grid tbody tr:nth-child(even).selected-row {
  background-color: var(--color-darker-blue);
}

.zs-grid tbody tr:nth-child(odd).selected-row {
  background-color: var(--color-light-blue);
}

.zs-grid tbody tr.row-hover:hover:nth-child(even) {
  background-color: #ffd1b0;
}

.zs-grid tbody tr.row-hover:hover:nth-child(odd) {
  background-color: #ffd1b0;
}

/* FIXED HEADER STYLES (Desktop) */
.zs-grid.fixed-header {
  overflow: hidden;
}

.zs-grid.fixed-header table {
  display: block;
  width: 100%;
}

.zs-grid.fixed-header thead {
  display: block;
}

.zs-grid.fixed-header tbody {
  display: block;
  overflow-y: auto;
  overflow-x: hidden;
}

.zs-grid.fixed-header tr,
.zs-grid.fixed-header tfoot tr {
  display: table;
  width: 100%;
  table-layout: fixed;
}


/* --- RESPONSIVE STYLES (Mobile) --- */
@media screen and (max-width: 768px) {

  /* Reset fixed-header styles to a block layout for card view */
  .zs-grid,
  .zs-grid table,
  .zs-grid thead,
  .zs-grid tbody,
  .zs-grid tr,
  .zs-grid td {
    display: block;
    width: 100% !important;
    height: auto !important;
    min-height: 0 !important;
    overflow: visible;
    /* Prevent unwanted scrollbars on elements */
  }

  /* When height is set on mobile, the root .zs-grid div becomes the scroll container */
  .zs-grid.fixed-header {
    height: 100%;
    overflow-y: auto;
  }

  /* CORRECTED: This rule is now specific enough to override other display properties */
  .zs-grid thead .zs-grid-header-row {
    display: none !important;
  }

  /* Card styles for each data row */
  .zs-grid tbody tr {
    border: 1px solid #ddd;
    margin-bottom: 1rem;
  }

  .zs-grid td {
    /* padding-left: 50%; */
    position: relative;
    text-align: right;
    border-bottom: 1px solid #eee;
  }

  .zs-grid td:last-child {
    border-bottom: none;
  }

  /* The label for each data point */
  .zs-grid td::before {
    content: attr(data-label);
    position: absolute;
    left: 10px;
    padding-right: 10px;
    width: 45%;
    white-space: nowrap;
    text-align: left;
    font-weight: bold;
    color: var(--color-dark-blue);
  }

  .zs-grid td.select-column {
    text-align: left;
    padding-left: 10px;
  }

  .zs-grid td.select-column::before {
    content: "";
    /* No label for the selection column */
  }

  .zs-grid .detail-row td,
  .zs-grid .footer-row td {
    padding-left: 1rem;
    text-align: left;
  }

  .zs-grid .detail-row td::before,
  .zs-grid .footer-row td::before {
    content: none;
  }
}

/* Style for the overflow indicator */
/* .zs-grid th.has-overflow::after {
  content: '…'; 
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: var(--zs-grid-overflow-bg);
  padding-left: 5px;
  font-weight: bold;
  color: var(--zs-grid-overflow-color);
} */

/* Column resize handle styles */
.zs-grid th {
  position: relative;
}

.zs-grid .resize-handle {
  position: absolute;
  top: 0;
  right: 5px;
  width: 4px;
  height: 100%;
  cursor: col-resize;
  background: lightgray;
  z-index: 10;
  transition: background-color, width 0.2s ease;
}

.zs-grid .resize-handle:hover {
  width: 8px;
  background: rgba(235, 118, 41, 0.3);
}

.zs-grid .resize-handle:active {
  background: rgba(235, 118, 41, 0.5);
}

.zs-grid .column-header {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5em;
}
</style>
